import { useState, type CSSProperties } from 'react';
import type { FinancialOverview } from './financial-overview-api';
import { remunerationMoney } from './RemunerationColumn';
import { WorkingCapitalSettingsDialog, monthlyAverageCents } from './WorkingCapitalSettingsDialog';
import { WorkingCapitalGoalProgress } from '@tosho/domain';
import { FinancialAmount, FinancialCalculationStatus } from './FinancialCalculationStatus';
import { FinanceExplanation } from './FinanceExplanation';
import settingsIcon from '../assets/finance/settings.svg?no-inline';
export { balanceForPeriod } from './financial-bank-balance';

const MONTHS = [
  'Jan.',
  'Fév.',
  'Mars',
  'Avr.',
  'Mai',
  'Juin',
  'Juil.',
  'Août',
  'Sept.',
  'Oct.',
  'Nov.',
  'Déc.',
] as const;

const money = remunerationMoney;

// Icône fournie en recette : mouvement entrant, symétrique de Rémunération.
function WorkingCapitalIcon(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M9.5 13.75C9.5 14.72 10.25 15.5 11.17 15.5H13.05C13.85 15.5 14.5 14.82 14.5 13.97C14.5 13.06 14.1 12.73 13.51 12.52L10.5 11.47C9.91 11.26 9.51001 10.94 9.51001 10.02C9.51001 9.17999 10.16 8.48999 10.96 8.48999H12.84C13.76 8.48999 14.51 9.26999 14.51 10.24" />
      <path d="M12 7.5V16.5" />
      <path d="M22 12C22 17.52 17.52 22 12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12 2" />
      <path d="M17 3V7H21" />
      <path d="M22 2L17 7" />
    </svg>
  );
}

export function balanceDelta(
  current: string | null,
  previous: string | null,
): { cents: string | null; percent: number | null } {
  if (current === null || previous === null) return { cents: null, percent: null };
  const difference = BigInt(current) - BigInt(previous);
  return {
    cents: difference.toString(),
    percent:
      BigInt(previous) === 0n
        ? null
        : Number(
            (difference * 10000n) / (BigInt(previous) < 0n ? -BigInt(previous) : BigInt(previous)),
          ) / 100,
  };
}

const directionLabels = {
  forward: 'Se rapproche',
  backward: 'Recule',
  stable: 'Stable',
  unknown: 'Progression inconnue',
} as const;

export function WorkingCapitalEvolutionCard({
  overview,
  demo = false,
  onSaveGoal,
}: {
  readonly overview: FinancialOverview;
  readonly demo?: boolean;
  readonly onSaveGoal?: ((cents: string) => Promise<void>) | undefined;
}): React.JSX.Element {
  const through = overview.observedThroughPeriod ?? overview.period;
  const currentYear = Number(through.slice(0, 4));
  const [year, setYear] = useState(Number(overview.period.slice(0, 4)));
  const [settings, setSettings] = useState(false);
  const [demoGoal, setDemoGoal] = useState<string | null>('1200000');
  const annualGoal = demo ? demoGoal : (overview.workingCapitalAnnualGoalCents ?? null);
  const rows = new WorkingCapitalGoalProgress(
    (overview.remuneration?.months ?? []).map((row) => ({
      period: row.period,
      ingoingsCents: row.ingoingsCents == null ? null : BigInt(row.ingoingsCents),
      outgoingsCents: row.outgoingsCents == null ? null : BigInt(row.outgoingsCents),
    })),
  ).year(year, through, annualGoal === null ? null : BigInt(annualGoal));
  const visibleRows = rows.filter((row) => row.period >= '2020-09');
  const observed = visibleRows.filter((row) => row.status !== 'future');
  const latest = observed.at(-1);
  const maximum = Math.max(
    1,
    Number(annualGoal ?? 0),
    ...rows.map((row) => Math.abs(Number(row.cumulativeCents ?? 0))),
  );
  // Conserver une échelle identique pour les deux signes sans réserver un
  // demi-graphe vide lorsque le recul cumulé est faible ou absent.
  const largestNegative = Math.max(0, ...rows.map((row) => -Number(row.cumulativeCents ?? 0)));
  const negativePlotHeight =
    largestNegative > 0 ? Math.max(2, (largestNegative / maximum) * 80) : 0;
  const percentage = (points: bigint | null | undefined): string =>
    points == null
      ? '—'
      : `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(Number(points) / 100)} %`;
  const signed = (cents: bigint | null): string =>
    cents === null ? '—' : `${cents > 0n ? '+' : ''}${money(cents.toString())}`;
  const missing = observed.some((row) => row.cumulativeCents === null);

  async function saveAnnualGoal(cents: string): Promise<void> {
    if (demo) {
      setDemoGoal(cents);
      return;
    }
    if (!onSaveGoal) throw new Error('Le réglage n’est pas disponible pour ce profil.');
    await onSaveGoal(cents);
  }

  return (
    <article
      className="working-capital-card finance-card--controls"
      aria-label={`Évolution du fond de roulement : ${year}`}
    >
      <header className="finance-card__heading">
        <h3 className="finance-card__title">
          <WorkingCapitalIcon />
          Évolution du fond de roulement
        </h3>
        <div className="finance-card__controls">
          <select
            aria-label="Année de l'évolution du fond de roulement"
            value={year}
            onChange={(event) => setYear(Number(event.currentTarget.value))}
          >
            {Array.from(
              { length: Math.max(currentYear, year) - 2019 },
              (_, index) => Math.max(currentYear, year) - index,
            ).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="working-capital-card__settings"
            aria-label="Configurer l’objectif du fond de roulement"
            title="Réglages de l’objectif annuel"
            onClick={() => setSettings(true)}
          >
            <img src={settingsIcon} alt="" />
          </button>
        </div>
      </header>
      <p>
        Progression de l’épargne de trésorerie vers votre objectif annuel
        {demo ? ' · simulation fictive' : ''}.
      </p>
      <div className="working-capital-card__summary">
        <div>
          <span>Cumul constitué en {year}</span>
          <strong>
            <FinancialAmount
              cents={latest?.cumulativeCents?.toString()}
              calculation="capitalGoal"
            />
          </strong>
          <small>Entrées moins sorties, rémunération déjà déduite</small>
        </div>
        <div>
          <span>Reste pour atteindre l’objectif</span>
          <strong>
            <FinancialAmount
              cents={latest?.remainingCents?.toString()}
              calculation={annualGoal === null ? 'settings' : 'capitalGoal'}
            />
          </strong>
        </div>
        <div>
          <span>Objectif annuel / mensuel</span>
          <strong>
            {annualGoal === null ? (
              <FinancialCalculationStatus
                calculation="settings"
                label="À définir dans les réglages"
              />
            ) : (
              `${money(annualGoal)} / ${money(monthlyAverageCents(annualGoal))}`
            )}
          </strong>
        </div>
        <div>
          <span>Objectif annuel atteint</span>
          <strong>
            {latest?.progressBasisPoints == null ? (
              <FinancialCalculationStatus
                calculation={annualGoal === null ? 'settings' : 'capitalGoal'}
              />
            ) : (
              percentage(latest.progressBasisPoints)
            )}
          </strong>
          <small>
            {latest?.flowCents == null
              ? 'Flux du dernier mois inconnu'
              : `${signed(latest.flowCents)} sur le dernier mois observé`}
          </small>
        </div>
      </div>
      <div
        className="working-capital-card__chart-scroll"
        role="region"
        aria-label={`Progression vers l’objectif annuel ${year}, défilement horizontal possible`}
        tabIndex={0}
      >
        <div
          className="working-capital-card__chart capital-goal-chart"
          style={{ '--capital-negative-height': `${negativePlotHeight}px` } as CSSProperties}
          role="group"
          aria-label={`Cumul annuel comparé à l’objectif ${year}`}
        >
          <div className="working-capital-card__zero" aria-hidden="true" />
          {annualGoal !== null && BigInt(annualGoal) > 0n ? (
            <div
              className="working-capital-card__goal"
              style={{ top: `${103 - (Number(annualGoal) / maximum) * 80}px` }}
            >
              <span>Objectif {money(annualGoal)}</span>
            </div>
          ) : null}
          {rows.map((row, index) => {
            const amount = Number(row.cumulativeCents ?? 0);
            const label = MONTHS[index]!;
            const description =
              row.period < '2020-09'
                ? 'Avant le début de l’activité'
                : row.status === 'future'
                  ? 'À venir'
                  : row.cumulativeCents === null
                    ? 'Cumul indisponible · raccordement S5'
                    : `${money(row.cumulativeCents.toString())} constitués, ${percentage(row.progressBasisPoints)} de l’objectif. ${directionLabels[row.direction]}.`;
            return (
              <div
                className={`working-capital-card__month capital-progress--${row.direction}`}
                key={row.period}
                aria-label={`${label} ${year} : ${description}`}
                title={description}
                data-direction={row.direction}
              >
                <div className="working-capital-card__plot">
                  {row.cumulativeCents === null ? (
                    <span className="working-capital-card__missing">—</span>
                  ) : (
                    <div
                      className={`working-capital-card__bar${amount < 0 ? ' is-negative' : ''}`}
                      style={{ height: `${Math.max(2, (Math.abs(amount) / maximum) * 80)}px` }}
                    />
                  )}
                </div>
                <span className="working-capital-card__month-label">{label}</span>
                <strong className="working-capital-card__amount">
                  {row.cumulativeCents === null ? '—' : money(row.cumulativeCents.toString())}
                </strong>
              </div>
            );
          })}
        </div>
      </div>
      {missing ? (
        <p className="working-capital-card__empty">
          <FinancialCalculationStatus calculation="capitalGoal" label="Cumul incomplet" /> Une
          période absente interrompt le cumul ; le flux d’un mois connu reste visible dans le
          tableau.
        </p>
      ) : null}
      <div className="working-capital-card__table-scroll">
        <table className="working-capital-card__table">
          <caption>Progression mois par mois vers l’objectif annuel</caption>
          <thead>
            <tr>
              <th>Mois</th>
              <th>Flux du mois</th>
              <th>Cumul constitué</th>
              <th>% de l’objectif</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.period} className={`capital-progress--${row.direction}`}>
                <th scope="row">
                  {MONTHS[Number(row.period.slice(5)) - 1]} {year}
                  <small>
                    {row.status === 'future' ? 'À venir' : directionLabels[row.direction]}
                    {row.goalReached ? ' · objectif atteint' : ''}
                  </small>
                </th>
                {row.status === 'future' ? (
                  <td colSpan={3}>À venir · réalisé non encore observable</td>
                ) : (
                  <>
                    <td>
                      {row.flowCents === null ? (
                        <FinancialCalculationStatus calculation="capitalGoal" />
                      ) : (
                        signed(row.flowCents)
                      )}
                    </td>
                    <td>
                      <FinancialAmount
                        cents={row.cumulativeCents?.toString()}
                        calculation="capitalGoal"
                      />
                    </td>
                    <td>
                      {row.progressBasisPoints === null ? (
                        <FinancialCalculationStatus
                          calculation={annualGoal === null ? 'settings' : 'capitalGoal'}
                        />
                      ) : (
                        percentage(row.progressBasisPoints)
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="working-capital-card__legend" aria-label="Sens de progression vers l’objectif">
        <li className="capital-progress--forward">
          <i />
          Se rapproche
        </li>
        <li className="capital-progress--backward">
          <i />
          Recule
        </li>
        <li className="capital-progress--stable">
          <i />
          Stable
        </li>
        <li className="capital-progress--unknown">
          <i />
          Inconnu ou à venir
        </li>
      </ul>
      <FinanceExplanation>
        <p>
          Chaque barre montre le cumul annuel des entrées moins les sorties importées, et non le
          solde du compte. Les sorties incluent déjà votre rémunération. Le suivi repart de zéro en
          janvier, ou en septembre pour l’année de démarrage 2020 ; il ne suppose pas un compte
          bancaire initialement à zéro.
        </p>
        <p>
          La couleur décrit le flux du mois : vert pour une progression positive, rouge pour un
          recul, gris pour une stabilité ou une valeur inconnue. Une lacune empêche d’affirmer le
          cumul et sa progression ; elle ne vaut jamais zéro. Le mois courant est provisoire, à la
          date des flux importés.
        </p>
        <p>
          L’objectif enregistré est le repère actuel, y compris lors de la lecture d’une ancienne
          année ; aucun historique de réglages n’est inventé. C’est un objectif d’épargne de
          trésorerie, pas un fonds de roulement comptable certifié ni un bénéfice.
        </p>
      </FinanceExplanation>
      {settings ? (
        <WorkingCapitalSettingsDialog
          annualGoalCents={annualGoal}
          suggestedReserveCents={overview.remuneration?.suggestedReserveCents ?? null}
          onSave={saveAnnualGoal}
          onCancel={() => setSettings(false)}
        />
      ) : null}
    </article>
  );
}
